/** Recurring / one-time cost calculation (solutioning bake-off). */

export const COST_CATEGORY_LABEL = {
  licensing: 'Licensing',
  hosting_saas: 'Hosting / SaaS',
  maintenance_support: 'Maintenance & support',
  support_staffing: 'Support staffing',
  other: 'Other',
};

export const BILLING_FREQUENCY_LABEL = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annual: 'Annual',
  one_time: 'One-time',
};

export const CAPEX_OPEX_LABEL = {
  capex: 'CapEx',
  opex: 'OpEx',
};

export function frequencyFactor(freq) {
  switch (freq) {
    case 'monthly': return 12;
    case 'quarterly': return 4;
    case 'annual':
    case 'one_time':
    default:
      return 1;
  }
}

/**
 * @param {object} line - recurring cost line
 * @param {number} defaultEscalationPct - project default (e.g. 3)
 * @param {number} years - TCO horizon (default 5)
 */
export function calcRecurringLineByYear(line, defaultEscalationPct = 3, years = 5) {
  const annualBase = Number(line.quantity || 0) * Number(line.unit_rate || 0) * frequencyFactor(line.billing_frequency);
  const escalation = (line.escalation_pct ?? line.cost_types?.default_escalation_pct ?? defaultEscalationPct) / 100;
  const start = line.start_year || 1;
  const end = line.end_year || years;
  const byYear = {};

  for (let y = 1; y <= years; y += 1) {
    if (y >= start && y <= end) {
      byYear[y] = Math.round(annualBase * (1 + escalation) ** (y - start) * 100) / 100;
    } else {
      byYear[y] = 0;
    }
  }
  return byYear;
}

/** Aggregate recurring lines into category × year matrix and totals. */
export function calcOptionRecurringCosts(lines, { defaultEscalationPct = 3, years = 5 } = {}) {
  const byYearCategory = {};
  for (let y = 1; y <= years; y += 1) {
    byYearCategory[y] = {};
  }

  let fiveYearTotal = 0;
  for (const line of lines || []) {
    const cat = line.cost_types?.category || line.category || 'other';
    const lineYears = calcRecurringLineByYear(line, defaultEscalationPct, years);
    for (let y = 1; y <= years; y += 1) {
      byYearCategory[y][cat] = (byYearCategory[y][cat] || 0) + (lineYears[y] || 0);
      fiveYearTotal += lineYears[y] || 0;
    }
  }

  return { byYearCategory, fiveYearTotal: Math.round(fiveYearTotal * 100) / 100 };
}

export function calcOneTimeTotals(lines) {
  let capex = 0;
  let opex = 0;
  for (const line of lines || []) {
    const amt = Number(line.amount || 0);
    if (line.capex_opex === 'capex') capex += amt;
    else opex += amt;
  }
  return {
    capex: Math.round(capex * 100) / 100,
    opex: Math.round(opex * 100) / 100,
    total: Math.round((capex + opex) * 100) / 100,
  };
}

/** Cash-basis TCO = one-time + 5yr recurring + effort ROM (if provided). */
export function calcOptionTCO({ oneTime, recurringTotal, effortCost = 0 }) {
  const oneTimeTotal = (oneTime?.capex || 0) + (oneTime?.opex || 0);
  return Math.round((oneTimeTotal + (recurringTotal || 0) + (effortCost || 0)) * 100) / 100;
}

export function formatCurrency(amount, currency = 'GBP') {
  const sym = currency === 'GBP' ? '£' : currency === 'EUR' ? '€' : currency === 'USD' ? '$' : '';
  return `${sym}${Number(amount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}
